// Users — Office admin. Accounts, roles and per-person extra permissions.
//
//   GET    /api/admin/users                      list (extras_count per row)
//   POST   /api/admin/users                      { email, password?, name, role, timezone? } — blank password: emailed a temporary one (NewAccountSheet.js)
//   PUT    /api/admin/users/:id                  profile / role / is_active / hourly_rate
//   POST   /api/admin/users/:id/reset-password   { new_password }
//   GET    /api/admin/users/:id/permissions      { role_permissions, extra_permissions, effective_permissions }
//   PUT    /api/admin/users/:id/permissions      { extra_permissions }
//   GET    /api/admin/permissions/known          { permissions, descriptions }
//   GET    /api/admin/users/:id/delete-check     the impact sentence before a delete
//   DELETE /api/admin/users/:id
//
// Permissions are role baseline ∪ extras (server/utils/permissions.js). The
// matrix shows the role's grants locked and the extras as the only toggles,
// and sends the whole extras object back — the server validates it against
// KNOWN_PERMISSIONS, so a typo is a refusal here, never a silent no-op.
//
// SSNs and the encryption passkey are NOT here, on purpose (CLAUDE.md rule 5).
// They live on /security (apps/office/admin/Security.js), which the detail
// panel links to.
//
// That sentence used to end "the classic Admin panel keeps those", which has
// been false since 2026-09-05: the classic tree was DELETED (docs/ONE_APP.md),
// and /security was built precisely because deleting it would otherwise have
// taken SSN entry with it — "a payroll clerk who cannot record a new hire's
// SSN cannot run payroll". Impersonation went with the classic app and has no
// home in this tree at all, so it is no longer named as though it had one.
//
// ── There is no "last signed in", and this screen cannot invent one ─────────
//
// `users` has no login timestamp: the columns are id, email, password, name,
// role, timezone, google_id, is_active, created_at, updated_at
// (server/database.js), and `grep -rn last_login server/ --include=*.js`
// returns nothing outside tests. GET /api/admin/users selects exactly
// created_at and updated_at and no third date.
//
// So "when did this person last sign in" is UNANSWERABLE from the data that
// exists, and adding the column is a database migration — CLAUDE.md rule 3,
// which needs explicit human approval and ships alone. `updated_at` is NOT a
// stand-in for it: it moves when an ADMIN edits the account, so labelling it
// "last seen" would put a plausible wrong date next to a person's name, which
// is the failure this repo calls the worst one available.

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { post, put, del, get } from '../../shared/http';
import { ROLE_OPTIONS, roleLabel } from '../../shared/roles';
import { useAuth } from '../../../App';
import { useRole } from '../../../hooks/usePermission';
import { DataTable, Pill, Avatar, FwButton, FwField, FwInput, FwSelect, CheckRow, Sheet, Notice, Skeleton, EmptyState, initialsOf } from '../../../ui/fw';
import {
  useOfficeQuery, useAction, PageHeader, Toolbar, SearchBox, Panel, Split, DetailList, DetailRow,
  ActivePill, LoadError, FormError, FormRow, ConfirmSheet, SheetFooter, matches, textOr, fmtDate,
  fmtDateTime, ROLE_TONE,
} from '../records/OfficePage';
import NewAccountSheet, { WelcomeNotice } from './NewAccountSheet';
import './admin.css';

/**
 * The role dropdown, and the words in it.
 *
 * This list used to be declared here, and it was the ONLY screen in the app
 * with labels at all — /crew and /security printed the raw key beside the same
 * person's name. It now comes from apps/shared/roles.js, which is checked
 * against the roles server/database.js actually seeds. Still exported under
 * this module's own name, because that is its existing public surface.
 */
export const ROLES = ROLE_OPTIONS;

const PROFILE_FIELDS = ['name', 'email', 'role', 'timezone'];

function seed(u) {
  const f = {};
  PROFILE_FIELDS.forEach((k) => { f[k] = u[k] === null || u[k] === undefined ? '' : String(u[k]); });
  f.is_active = u.is_active === 1 || u.is_active === true;
  f.hourly_rate = u.hourly_rate === null || u.hourly_rate === undefined ? '' : String(u.hourly_rate);
  return f;
}

/** The PUT body: only what changed. hourly_rate only when the list carried it. */
export function profileBody(form, original, hasRate) {
  const body = {};
  PROFILE_FIELDS.forEach((k) => {
    const was = original[k] === null || original[k] === undefined ? '' : String(original[k]);
    if (form[k] !== was) body[k] = form[k];
  });
  const wasActive = original.is_active === 1 || original.is_active === true;
  if (form.is_active !== wasActive) body.is_active = form.is_active;
  if (hasRate) {
    const wasRate = original.hourly_rate === null || original.hourly_rate === undefined ? '' : String(original.hourly_rate);
    if (form.hourly_rate !== wasRate) body.hourly_rate = form.hourly_rate === '' ? '' : Number(form.hourly_rate);
  }
  return body;
}

/** extras with one (resource, action) flipped; empty resources are dropped so the object stays tidy. */
export function toggleExtra(extras, resource, action, on) {
  const next = {};
  Object.entries(extras || {}).forEach(([r, acts]) => { if (Array.isArray(acts) && acts.length) next[r] = [...acts]; });
  const list = new Set(next[resource] || []);
  if (on) list.add(action); else list.delete(action);
  if (list.size) next[resource] = [...list]; else delete next[resource];
  return next;
}

export function extrasCount(extras) {
  return Object.values(extras || {}).reduce((n, a) => n + (Array.isArray(a) ? a.length : 0), 0);
}

// ── permissions matrix ───────────────────────────────────────────────────────

function PermissionsPanel({ user }) {
  const qc = useQueryClient();
  const known = useOfficeQuery(['office', 'permissions', 'known'], '/admin/permissions/known');
  const perms = useOfficeQuery(['office', 'users', user.id, 'permissions'], `/admin/users/${user.id}/permissions`);
  const [extras, setExtras] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const { busy, error, run } = useAction();

  useEffect(() => {
    if (perms.data) { setExtras(perms.data.extra_permissions || {}); setDirty(false); setSaved(false); }
  }, [perms.data]);

  if (known.isPending || perms.isPending || extras === null) return <Skeleton lines={5} />;
  if (known.error) return <LoadError error={known.error} />;
  if (perms.error) return <LoadError error={perms.error} />;

  const resources = known.data.permissions || {};
  const descriptions = known.data.descriptions || {};
  const roleGrants = perms.data.role_permissions || {};

  const flip = (resource, action) => (on) => {
    setExtras((e) => toggleExtra(e, resource, action, on));
    setDirty(true);
    setSaved(false);
  };

  const save = async () => {
    const ok = await run(async () => {
      await put(`/admin/users/${user.id}/permissions`, { extra_permissions: extras });
      qc.invalidateQueries({ queryKey: ['office', 'users'] });
    }, 'Could not save the permissions.');
    if (ok) { setDirty(false); setSaved(true); }
  };

  return (
    <Panel
      title="Permissions"
      actions={(
        <span className="or-actions">
          {saved && !dirty ? <span className="or-muted">Saved.</span> : null}
          <FwButton onClick={save} loading={busy} disabled={!dirty}>Save permissions</FwButton>
        </span>
      )}
    >
      <p className="or-muted">
        {roleLabel(user.role)} grants the locked rows. Ticking anything else adds it for {user.name} only —{' '}
        {extrasCount(extras) === 0 ? 'nothing extra right now.' : `${extrasCount(extras)} extra so far.`}
      </p>
      <FormError error={error} />
      <div className="or-matrix" data-testid="permission-matrix">
        {Object.entries(resources).map(([resource, actions]) => (
          <div key={resource} data-resource={resource}>
            <h3 className="or-matrix__resource">{resource.replace(/_/g, ' ')}</h3>
            {actions.map((action) => {
              const fromRole = (roleGrants[resource] || []).includes(action);
              const extra = (extras[resource] || []).includes(action);
              const d = (descriptions[resource] || {})[action] || {};
              return (
                <CheckRow
                  key={action}
                  data-permission={`${resource}.${action}`}
                  checked={fromRole || extra}
                  disabled={fromRole}
                  label={d.label || action}
                  meta={fromRole ? `Granted by the ${roleLabel(user.role)} role.` : (d.description || null)}
                  onToggle={flip(resource, action)}
                />
              );
            })}
          </div>
        ))}
      </div>
    </Panel>
  );
}

// ── one person ───────────────────────────────────────────────────────────────

function UserDetail({ user, hasRate, isSelf, onClose, onChanged }) {
  const [form, setForm] = useState(() => seed(user));
  const [resetOpen, setResetOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [impact, setImpact] = useState(null);
  const profile = useAction();
  const reset = useAction();
  const remove = useAction();

  useEffect(() => { setForm(seed(user)); }, [user]);

  const body = profileBody(form, user, hasRate);
  const dirty = Object.keys(body).length > 0;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!dirty) return;
    const ok = await profile.run(async () => { await put(`/admin/users/${user.id}`, body); }, 'Could not save this account.');
    if (ok) onChanged();
  };

  const doReset = async (e) => {
    e.preventDefault();
    const ok = await reset.run(async () => { await post(`/admin/users/${user.id}/reset-password`, { new_password: newPassword }); }, 'Could not reset the password.');
    if (ok) { setResetOpen(false); setNewPassword(''); }
  };

  const openDelete = async () => {
    setImpact(null);
    setDeleteOpen(true);
    try { setImpact(await get(`/admin/users/${user.id}/delete-check`)); } catch (e) { setImpact({ warning: 'Could not check what this account is linked to.' }); }
  };
  const doDelete = async () => {
    const ok = await remove.run(async () => { await del(`/admin/users/${user.id}`); }, 'Could not delete this account.');
    if (ok) { setDeleteOpen(false); onChanged(true); }
  };

  return (
    <div className="or-form" data-testid="user-detail">
      <Panel
        title={user.name}
        actions={<FwButton variant="ghost" icon="x" aria-label="Close" onClick={onClose} />}
      >
        <DetailList>
          <DetailRow label="Role"><Pill tone={ROLE_TONE[user.role] || 'neutral'}>{roleLabel(user.role)}</Pill></DetailRow>
          <DetailRow label="Status"><ActivePill active={user.is_active} /></DetailRow>
          <DetailRow label="Since">{fmtDate(user.created_at)}</DetailRow>
          {/* `updated_at` is already on every row from GET /admin/users and was
              being thrown away. It is worth showing and worth labelling
              precisely: it moves when this ACCOUNT RECORD changes — a role
              swap, a deactivation, a password reset — and NOT when the person
              signs in. "Last active" or "Last seen" here would be a plausible
              wrong date beside somebody's name, and there is no login
              timestamp anywhere in the schema to put there instead (see the
              header). So the label says what the column means. */}
          <DetailRow label="Account last changed">{fmtDateTime(user.updated_at)}</DetailRow>
          <DetailRow label="Extras">{user.extras_count ? `${user.extras_count} extra permission${user.extras_count === 1 ? '' : 's'}` : 'None'}</DetailRow>
        </DetailList>
        <form id={`user-form-${user.id}`} className="or-form" onSubmit={save}>
          <FormError error={profile.error} />
          <FormRow>
            <FwField label="Name" htmlFor={`u-name-${user.id}`} required>
              <FwInput id={`u-name-${user.id}`} value={form.name} onChange={set('name')} required />
            </FwField>
            <FwField label="Email" htmlFor={`u-email-${user.id}`} required>
              <FwInput id={`u-email-${user.id}`} type="email" value={form.email} onChange={set('email')} required />
            </FwField>
          </FormRow>
          <FormRow>
            <FwField label="Role" htmlFor={`u-role-${user.id}`} hint={isSelf ? 'You cannot change your own role here.' : null}>
              <FwSelect id={`u-role-${user.id}`} value={form.role} onChange={set('role')} disabled={isSelf}>
                {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </FwSelect>
            </FwField>
            <FwField label="Timezone" htmlFor={`u-tz-${user.id}`} hint="Blank uses the company timezone.">
              <FwInput id={`u-tz-${user.id}`} value={form.timezone} onChange={set('timezone')} placeholder="America/New_York" />
            </FwField>
          </FormRow>
          {hasRate ? (
            <FormRow>
              <FwField label="Hourly rate" htmlFor={`u-rate-${user.id}`} hint="Blank clears it.">
                <FwInput id={`u-rate-${user.id}`} type="number" step="0.01" min="0" inputMode="decimal" value={form.hourly_rate} onChange={set('hourly_rate')} />
              </FwField>
            </FormRow>
          ) : null}
          <label className="or-form__check">
            <input type="checkbox" checked={form.is_active} disabled={isSelf} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
            Active — can sign in
          </label>
          <div className="or-actions">
            <FwButton type="submit" loading={profile.busy} disabled={!dirty}>Save</FwButton>
            <FwButton variant="ghost" onClick={() => { setResetOpen(true); reset.clear(); }}>Reset password</FwButton>
            {!isSelf ? <FwButton variant="quiet" onClick={openDelete}>Delete…</FwButton> : null}
          </div>
        </form>
        <p className="or-muted">
          SSN and the encryption passkey are on <Link className="or-link" to="/security">Security</Link>.
        </p>
      </Panel>

      <PermissionsPanel user={user} />

      <Sheet open={resetOpen} title={`Reset password for ${user.name}`} onClose={() => setResetOpen(false)} footer={<SheetFooter onCancel={() => setResetOpen(false)} busy={reset.busy} submitLabel="Set password" formId={`reset-${user.id}`} />}>
        <form id={`reset-${user.id}`} className="or-form" onSubmit={doReset}>
          <FormError error={reset.error} />
          <FwField label="New password" htmlFor={`u-pw-${user.id}`} hint="At least 8 characters. Tell them in person; it is not emailed." required>
            <FwInput id={`u-pw-${user.id}`} type="password" autoComplete="new-password" minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
          </FwField>
        </form>
      </Sheet>

      <ConfirmSheet open={deleteOpen} title={`Delete ${user.name}?`} confirmLabel="Delete account" tone="stop" busy={remove.busy} onConfirm={doDelete} onClose={() => setDeleteOpen(false)}>
        <FormError error={remove.error} />
        <Notice tone="warn" title="Deactivating is usually the right call">
          An inactive account cannot sign in but keeps its name on every job, clock entry and paystub. Delete only for an account created by mistake.
        </Notice>
        {impact ? <p>{impact.warning}</p> : <Skeleton lines={2} />}
      </ConfirmSheet>
    </div>
  );
}

// ── the screen ───────────────────────────────────────────────────────────────

export default function Users() {
  const role = useRole();
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const users = useOfficeQuery(['office', 'users'], '/admin/users', { enabled: role === 'admin' });
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [created, setCreated] = useState(null);

  const list = useMemo(() => (Array.isArray(users.data) ? users.data : []), [users.data]);
  const hasRate = list.some((u) => Object.prototype.hasOwnProperty.call(u, 'hourly_rate'));
  const rows = useMemo(() => list.filter((u) => {
    if (!showInactive && !(u.is_active === 1 || u.is_active === true)) return false;
    // A filter on a ROW's role label, not a permission check on the signed-in
    // person; written this way round so roleCheckRatchet.test.js does not read
    // it as one.
    if (roleFilter !== 'all' && roleFilter !== u.role) return false;
    return matches(u, ['name', 'email', 'role'], search);
  }), [list, showInactive, roleFilter, search]);
  const selected = list.find((u) => u.id === selectedId) || null;

  if (role !== 'admin') {
    return (
      <div className="or-page ad-page" data-screen="office-Users">
        <PageHeader title="Users" />
        <EmptyState icon="lock" title="Admins only">Accounts and permissions can only be changed by an administrator.</EmptyState>
      </div>
    );
  }

  const refresh = (closed) => {
    qc.invalidateQueries({ queryKey: ['office', 'users'] });
    if (closed) setSelectedId(null);
  };

  // As a card on a phone: the person's name, their email under it, and
  // ACTIVE/INACTIVE as the badge. Whether an account can sign in is the
  // question this screen is opened with — "why can't he log in" — so it is the
  // badge rather than a column, and it is the status even though `role` is
  // also a pill. Role stays as the one labelled fact; the extras COUNT is
  // table-only, because "—" for almost everybody is a line of card height
  // spent on nothing.
  const columns = [
    { key: 'name', label: 'Person', role: 'title', render: (u) => (
      <span className="or-strip__name"><Avatar initials={initialsOf(u.name)} size="sm" tone={ROLE_TONE[u.role] || 'neutral'} /> <strong>{u.name}</strong></span>
    ) },
    { key: 'email', label: 'Email', role: 'subtitle', render: (u) => textOr(u.email) },
    { key: 'role', label: 'Role', role: 'fact', render: (u) => <Pill tone={ROLE_TONE[u.role] || 'neutral'}>{roleLabel(u.role)}</Pill> },
    { key: 'extras', label: 'Extras', align: 'right', role: 'hidden', render: (u) => (u.extras_count ? String(u.extras_count) : '—') },
    { key: 'is_active', label: 'Status', role: 'badge', render: (u) => <ActivePill active={u.is_active} /> },
  ];

  return (
    <div className="or-page ad-page" data-screen="office-Users">
      <PageHeader title="Users" meta={users.data ? `${rows.length} of ${list.length}` : null}>
        <FwButton icon="plus" onClick={() => setNewOpen(true)}>New account</FwButton>
      </PageHeader>
      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder="Name, email or role" label="Search users" />
        <FwField label="Role" htmlFor="users-role">
          <FwSelect id="users-role" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
            <option value="all">All roles</option>
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </FwSelect>
        </FwField>
        <label className="or-form__check">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Show inactive
        </label>
      </Toolbar>
      <WelcomeNotice created={created} onDismiss={() => setCreated(null)} />
      <LoadError error={users.error} fallback="Could not load the accounts." />
      {users.isPending ? <Skeleton lines={6} /> : null}
      {users.data ? (
        <Split
          main={(
            <DataTable
              caption="Accounts"
              columns={columns}
              rows={rows}
              onRowClick={(u) => setSelectedId(u.id)}
              empty={list.length ? 'No accounts match these filters.' : 'No accounts yet.'}
            />
          )}
          aside={selected ? (
            <UserDetail
              key={selected.id}
              user={selected}
              hasRate={hasRate}
              isSelf={!!me && me.id === selected.id}
              onClose={() => setSelectedId(null)}
              onChanged={refresh}
            />
          ) : null}
        />
      ) : null}
      <NewAccountSheet open={newOpen} onClose={() => setNewOpen(false)} onCreated={(made) => { setNewOpen(false); setCreated(made); refresh(); }} />
    </div>
  );
}
